import React from "react";
import { useTranslation } from "react-i18next";
import { addDays, addHours, endOfDay, startOfDay, format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faCalendarDays,
  faCalendarWeek,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import InputField from "../../components/InputField";

const formatLocalDateTime = (date) => {
  if (!date) {
    return "";
  }

  return format(date, "yyyy-MM-dd'T'HH:mm");
};

const InlineDateField = ({ label, value, onChange }) => {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-xs font-semibold uppercase tracking-wide text-mute-500">
        {label}
      </label>
          <input
              type="datetime-local"
              className="input-default w-full min-w-0 max-w-full appearance-none"
              style={{ minWidth: 0, maxWidth: "100%" }}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
          />
    </div>
  );
};

const BookingFilters = ({
  search,
  setSearch,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onQuickRangeSelect,
  onSearch,
  isSearching,
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-2xl border border-mute-200 bg-white shadow-lg p-4 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-sm text-mute-500 mt-1">
            {t("booking.filterHelp")}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            className="btn-sm btn-outline-primary w-full justify-center sm:w-auto"
            onClick={() => {
              const today = new Date();
              onQuickRangeSelect?.(startOfDay(today), endOfDay(today));
            }}
          >
            <FontAwesomeIcon icon={faCalendarDay} /> {t("dateRange.today")}
          </button>
          <button
            type="button"
            className="btn-sm btn-outline-primary w-full justify-center sm:w-auto"
            onClick={() => {
              const now = new Date();
              onQuickRangeSelect?.(now, addHours(now, 24));
            }}
          >
            <FontAwesomeIcon icon={faCalendarDays} /> {t("booking.next24h")}
          </button>
          <button
            type="button"
            className="btn-sm btn-outline-primary w-full justify-center sm:w-auto"
            onClick={() => {
              const now = new Date();
              onQuickRangeSelect?.(now, addDays(now, 7));
            }}
          >
            <FontAwesomeIcon icon={faCalendarWeek} /> {t("booking.next7days")}
          </button>
        </div>
      </div>

      <form className="mt-6 flex flex-col gap-4" onSubmit={onSearch}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
          <div className="min-w-0 lg:col-span-5">
            <InputField
              icon={faSearch}
              placeholder={t("booking.searchPlaceholder")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 min-w-0 lg:col-span-5">
            <InlineDateField
              label={t("common.start")}
              value={formatLocalDateTime(startDate)}
              onChange={(value) => {
                const next = value ? new Date(value) : null;
                setStartDate(next);
              }}
            />
            <InlineDateField
              label={t("common.end")}
              value={formatLocalDateTime(endDate)}
              onChange={(value) => {
                const next = value ? new Date(value) : null;
                setEndDate(next);
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-full justify-center lg:col-span-2 lg:self-auto"
            disabled={isSearching || !startDate || !endDate}
          >
            <span className="flex items-center justify-center gap-2">
              <FontAwesomeIcon icon={faSearch} />
              {t("common.search")}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookingFilters;
